/**
 * Helper functions for the AI-first tour
 * These mock AI interactions and tab switching without making real API calls
 */

import { EditorView } from "@codemirror/view";
import { demoImages, getDemoPresentationCode, strings } from "@quickpoint.me/shared";
import { fireAiStreamChunkEvent, fireAiStreamEndEvent, fireAiStreamStartEvent, goToPage } from "../../../editor/events";
import { authorReceiveBC } from "../../../messageBus";
import { readOnlyBoundaryField } from "../../../editor/chatReadOnlyExtension";

// Slower speed for the inline edit demo so the user can follow along
const EDIT_TYPE_SPEED = process.env.NODE_ENV === "test" ? 1 : 80;

let editTyper: NodeJS.Timeout;

/**
 * Gets the chat CodeMirror EditorView from the DOM
 */
function getChatEditorView(): EditorView | null {
	const cmEditor = document.querySelector(".chat-codemirror .cm-editor");
	if (!cmEditor) return null;
	return EditorView.findFromDOM(cmEditor as HTMLElement);
}

/**
 * Inserts text into the chat CodeMirror in one go, without scrolling.
 */
export function simulateTyping(
	text: string,
	onComplete?: () => void,
): void {
	const view = getChatEditorView();
	if (!view || text.length === 0) {
		onComplete?.();
		return;
	}

	// Clear any existing user input (everything after the read-only boundary)
	const boundary = view.state.field(readOnlyBoundaryField);
	const docLen = view.state.doc.length;

	view.dispatch({
		changes: { from: boundary, to: docLen, insert: text },
		selection: { anchor: boundary + text.length },
	});

	// Scroll back to top so the CM doesn't jump down
	view.dispatch({ effects: EditorView.scrollIntoView(0) });

	onComplete?.();
}

/**
 * Clears any ongoing simulated typing (chat and inline edit)
 */
export function clearTyping(): void {
	clearInterval(editTyper);
	clearInterval(aiTyper);
}

/**
 * Gets the inline element editor's CodeMirror EditorView from the DOM
 */
function getInlineEditorView(): EditorView | null {
	const cmEditor = document.querySelector(".inline-codemirror .cm-editor");
	if (!cmEditor) return null;
	return EditorView.findFromDOM(cmEditor as HTMLElement);
}

/**
 * Simulates editing text in the inline element editor.
 * Selects from `fromCol` to end of the value (after "title: "),
 * deletes it, then types the replacement slowly.
 * Sends goToPage after each keystroke to keep the viewer on the
 * correct page (the structure CM's cursor-based navigation resets it).
 */
export function simulateInlineEdit(
	replacement: string,
	fromCol: number,
	hostId: string,
	pageIndex: number,
): void {
	clearInterval(editTyper);
	const characters = Array.from(replacement);

	const view = getInlineEditorView();
	if (!view) return;

	const docLen = view.state.doc.length;

	// Select from fromCol to end, then delete
	view.dispatch({
		changes: { from: fromCol, to: docLen, insert: "" },
		selection: { anchor: fromCol },
	});

	let idx = 0;
	editTyper = setInterval(() => {
		if (!characters[idx]) {
			clearInterval(editTyper);
			return;
		}
		const currentView = getInlineEditorView();
		if (!currentView) {
			clearInterval(editTyper);
			return;
		}
		const end = currentView.state.doc.length;
		currentView.dispatch({
			changes: { from: end, insert: characters[idx] },
			selection: { anchor: end + characters[idx].length },
		});
		// Keep the viewer on the correct page after each keystroke
		goToPage(hostId, "QP", pageIndex);
		idx++;
	}, EDIT_TYPE_SPEED);
}

/**
 * Switches to a specific chat level tab
 * @param level 1 = Chat, 2 = Edit Element, 3 = Structure
 */
export function switchToLevel(level: 1 | 2 | 3): void {
	const tabIds = {
		1: "tab-chat",
		2: "tab-edit-element",
		3: "tab-structure",
	};
	const tab = document.getElementById(tabIds[level]);
	if (tab && !tab.getAttribute("aria-disabled")) {
		tab.click();
	}
}

/**
 * Returns the locale-aware demo presentation code from shared
 */
export function getDemoPresentation(): string {
	return getDemoPresentationCode();
}

/**
 * Preload demo images so they're cached before the demo presentation is injected.
 * Extracts image names from the demo code, matches them against demoImages, and
 * triggers browser fetches via `new Image()`.
 */
export function preloadDemoImages(): void {
	const imageNames = [...getDemoPresentation().matchAll(/^image:\s*(.+)$/gm)].map(m => m[1].trim());
	for (const name of imageNames) {
		const match = demoImages.find(img => img.userDefinedName === name);
		if (match?.url) {
			const img = new Image();
			img.src = match.url;
		}
	}
}

/**
 * Injects the demo presentation into the editor using the same AI stream
 * events that the real AI response path uses (aiStreamStart → aiStreamChunk
 * → aiStreamEnd). This ensures the code flows through the normal CM
 * transaction pipeline: annotated as a user event → onTransaction →
 * setCodeMirrorCodeState + setNewCode → StackAtomFamily update.
 */
export function injectDemoPresentation(_hostId: string): void {
	fireAiStreamStartEvent();
	fireAiStreamChunkEvent(getDemoPresentation());
	fireAiStreamEndEvent();
}

// Mock AI rationale is now in text.tourSteps.demoMockRationale

// Speed for the simulated AI response typing (faster than user typing)
const AI_TYPE_SPEED = process.env.NODE_ENV === "test" ? 1 : 8;

let aiTyper: NodeJS.Timeout;

/**
 * Custom event name used to stream a mock AI response into the
 * ChatCodeMirror's Recoil-backed history during the tour demo.
 */
export const STREAM_AI_EVENT = "tour:stream-ai";

export interface StreamAiDetail {
	userPrompt: string;
	aiResponse: string;
}

/**
 * Dispatches a custom DOM event that ChatCodeMirror listens for.
 * The handler adds the user message immediately and streams the
 * AI response character by character into AiChatHistoryAtom.
 */
export function streamMockAiResponse(userPrompt: string, aiResponse: string): void {
	window.dispatchEvent(
		new CustomEvent<StreamAiDetail>(STREAM_AI_EVENT, {
			detail: { userPrompt, aiResponse },
		}),
	);
}

/**
 * Clears the AI response streaming interval.
 */
export function clearAiTyping(): void {
	clearInterval(aiTyper);
}

/**
 * Called by the ChatCodeMirror event handler to drive the character-
 * by-character streaming. Returns a cleanup function.
 */
export function startAiStreaming(
	aiResponse: string,
	onChar: (textSoFar: string) => void,
	onDone: () => void,
): () => void {
	clearInterval(aiTyper);
	const characters = Array.from(aiResponse);
	let idx = 0;
	let buffer = "";
	aiTyper = setInterval(() => {
		if (idx >= characters.length) {
			clearInterval(aiTyper);
			onDone();
			return;
		}
		buffer += characters[idx];
		onChar(buffer);
		idx++;
	}, AI_TYPE_SPEED);
	return () => clearInterval(aiTyper);
}

/**
 * Gets the chat CodeMirror container for targeting
 */
export function getChatContainer(): HTMLElement | null {
	return document.querySelector(".chat-codemirror");
}

/**
 * Focus the chat CodeMirror input
 */
export function focusChatInput(): void {
	const view = getChatEditorView();
	if (view) {
		view.focus();
	}
}

/**
 * Simulates clicking an element in the preview, which triggers the
 * inline element editor (Level 2). Dispatches a MessageEvent directly
 * on the author's BroadcastChannel (postMessage only reaches other
 * contexts, so we use dispatchEvent to trigger the local onmessage handler).
 */
export function simulateElementClick(lineNumber: number, pageIndex: number, elementType: string): void {
	if (!authorReceiveBC) return;
	const data = {
		hostId: strings.broadcastChannel.allAuthors,
		type: "QP",
		action: strings.messages.elementClick,
		payload: { lineNumber, pageIndex, elementType, content: "" },
	};
	authorReceiveBC.dispatchEvent(new MessageEvent("message", { data }));
}
